import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

/**
 * Base class for multiplication poster generators (Nasobilka Plakaty)
 * Creates visual multiplication table posters with grid representations
 */
class Nasobilka_Plakaty_Base implements ILayoutBaseGen {
  constructor(private readonly blackAndWhite: boolean) {}

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const margin = PdfUnit.FromMillimeters(3);
    const marginOut = PdfUnit.FromMillimeters(10);
    const baseFont = new PdfFont(PdfFont.FONT_NAME, 100);
    const numberFont = new PdfFont(PdfFont.FONT_NAME, 24);
    const {A4} = PdfSize;

    // Color palette for rows (gradient from teal to orange)
    const colors = [
      '#7CBFC3',
      '#93C6B1',
      '#A8CC9D',
      '#BDD286',
      '#D1D769',
      '#E0D258',
      '#E8C35A',
      '#F0B45B',
      '#F6A45D',
      '#FB945D',
    ].map((hex) => PdfColor.fromHex(hex));

    const basePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);

    // Generate 100 pages (10x10 multiplication table)
    for (let b = 1; b <= 10; b++) {
      for (let a = 1; a <= 10; a++) {
        const page = pdf.addPage();

        const top = `${a} × ${b} = ${a * b}`;
        const bottom = `${a * b} : ${b} = ${a}`;

        // Adjust font to fit text
        const f = page.adjustFont(A4.width - PdfUnit.FromMillimeters(20), [top, bottom], baseFont);

        // Calculate rectangles for text and grid
        const topRect = new PdfRect(0, A4.height / 10, A4.width, (A4.height / 10) * 2);
        const middleRect = new PdfRect(0, (A4.height / 10) * 3, A4.width, (A4.height / 10) * 5);
        const bottomRect = new PdfRect(0, (A4.height / 10) * 8, A4.width, (A4.height / 10) * 2);

        // Draw text labels
        page.drawString(top, f, PdfBrush.Black, null, topRect, ETextAlign.Center);
        page.drawString(bottom, f, PdfBrush.Black, null, bottomRect, ETextAlign.Center);

        // Calculate grid cell size
        const sizeA = (middleRect.width - (a - 1) * margin - 2 * marginOut) / a;
        const sizeB = (middleRect.height - (b - 1) * margin) / b;
        const size = Math.min(sizeA, sizeB);

        // Calculate grid origin (centered)
        const originX =
          middleRect.x +
          marginOut +
          (middleRect.width - ((a - 1) * margin + a * size + 2 * marginOut)) / 2;
        const originY = middleRect.y + (middleRect.height - ((b - 1) * margin + b * size)) / 2;

        // Draw the grid
        for (let x = 0; x < a; x++) {
          for (let y = 0; y < b; y++) {
            const number = y * a + x + 1;
            const r = new PdfRect(
              originX + x * (size + margin),
              originY + y * (size + margin),
              size,
              size
            );

            if (this.blackAndWhite) {
              // Black and white: outline circles with numbers
              page.drawEllipse(basePen, null, r);
              page.drawString(
                number.toString(),
                numberFont,
                PdfBrush.Black,
                null,
                r,
                ETextAlign.Center
              );
            } else {
              // Color: filled circles with white numbers
              const colorBrush = new PdfBrush(colors[y]);
              page.drawEllipse(null, colorBrush, r);
              page.drawString(
                number.toString(),
                numberFont,
                PdfBrush.White,
                null,
                r,
                ETextAlign.Center
              );
            }
          }
        }
      }
    }
  }
}

/**
 * Colored multiplication poster generator
 * Creates 100 pages showing multiplication and division facts with colored grids
 */
export class Nasobilka_Plakaty__Barevne extends Nasobilka_Plakaty_Base {
  constructor() {
    super(false);
  }
}

/**
 * Black and white multiplication poster generator
 * Creates 100 pages showing multiplication and division facts with outlined grids
 */
export class Nasobilka_Plakaty__Cernobile extends Nasobilka_Plakaty_Base {
  constructor() {
    super(true);
  }
}
